import dayjs, { extend as dayjsExtend } from 'dayjs'
import 'dayjs/locale/fr'
import 'dayjs/locale/en'
import 'dayjs/locale/de'
import 'dayjs/locale/es'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import i18n from '@/plugins/i18n'

dayjsExtend(relativeTime)
dayjsExtend(localizedFormat)
dayjsExtend(utc)
dayjsExtend(timezone)

const DATE_FORMAT = 'YYYY-MM-DD'

/**
 * Returns a DD/MM/YYYY date
 * @param {string} date
 * @returns {string} DD/MM/YYYY date. Sample Output: "18/05/2020"
 * */
function ISOToDDMMYYYY (date) {
  if (date) {
    return dayjs(date).format('DD/MM/YYYY')
  } else {
    return ''
  }
}
/**
 * Returns a YYYY-MM-DD date
 * @param {string} date
 * @returns {string} YYYY-MM-DD date. Sample Output: "2020-05-18"
 * */
function ISOToYYYYMMDD (date) {
  if (date) {
    return dayjs(date).format('YYYY-MM-DD')
  } else {
    return ''
  }
}

function extractOnlyDateFromDateTime (date) {
  return dayjs(date).format('YYYY-MM-DD')
}

function extractOnlyTimeFromDateTime (date) {
  return dayjs(date).format('HH:mm')
}

/**
 * Returns a duration from now
 * @param {string} date
 * @param {boolean} withoutSuffix
 * @returns {string} Duration from now. Sample Output: "4 days ago"
 * */
function ISOToRelativeTime (date, withoutSuffix = false) {
  if (date) {
    return dayjs(date).fromNow(withoutSuffix)
  } else {
    return ''
  }
}

/**
 * Returns a shortened date
 * @param {string} date
 * @returns {string} Shortened date. Sample Output: "16 Nov 2020"
 * */
function ISOToShortenedDate (date) {
  if (date) {
    return dayjs(date).format('ll')
  } else {
    return ''
  }
}

function ISOToShortDate (date, options = {}) {
  if (!date) {
    return
  }

  const preposition = i18n.global.t('common.dateUtils.preposition')
  let format = `ll [${preposition}] LT`

  if (options.withArticle) {
    const article = i18n.global.t('common.dateUtils.article').toLowerCase()
    format = `[${article}] ` + format
  }

  return dayjs(date).format(format)
}

function ISOToWeekDay (date) {
  if (date) {
    return dayjs(date).format('dddd')
  } else {
    return ''
  }
}

function dateToStringDateTime (date) {
  if (!date) {
    return null
  }

  const dayjsDate = dayjs(date)
  return dayjsDate.isValid()
    ? dayjsDate.format('YYYY-MM-DD HH:mm:ss')
    : null
}

function formatDateValue (dateValue) {
  if (!Array.isArray(dateValue)) { return ISOToShortenedDate(dateValue) }

  if (dateValue.length === 1) {
    return ISOToShortenedDate(dateValue[0])
  } else {
    const dateRange = dateValue.map(date => ISOToShortenedDate(date)).join(' - ')
    return dateRange
  }
}

function getRelativePastDateRange (number, timeUnit) {
  const today = dayjs()
  const dayOfXTimeAgo = today.subtract(number, timeUnit)
  const endDate = ISOToYYYYMMDD(today)
  const startDate = ISOToYYYYMMDD(dayOfXTimeAgo)
  return { begin: startDate, end: endDate }
}

function isToday(date) {
  return dayjs(date).isSame(dayjs(), 'day')
}

export {
  DATE_FORMAT,
  ISOToDDMMYYYY,
  ISOToRelativeTime,
  ISOToShortDate,
  ISOToShortenedDate,
  ISOToWeekDay,
  ISOToYYYYMMDD,
  dateToStringDateTime,
  extractOnlyDateFromDateTime,
  extractOnlyTimeFromDateTime,
  formatDateValue,
  getRelativePastDateRange,
  isToday,
}
